
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from '@/integrations/supabase/client';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CheckCircle, XCircle, Bell, RefreshCw, Clock, FileText, Edit, Building } from "lucide-react";
import { Link } from "react-router-dom";
import { ProposalAcceptanceService } from "@/services/proposalAcceptanceService";
import type { Json } from "@/integrations/supabase/types";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { SidebarInset } from "@/components/ui/sidebar";
import { TermsAcknowledgment } from "@/components/common/TermsAcknowledgment";
import { useMarkDealAsLost } from "@/hooks/use-deals";
import { ProposalAcceptanceSection } from "@/components/common/ProposalAcceptanceSection";
import { useOrganizationProfileNotifications, useDismissOrganizationProfileNotification } from "@/hooks/use-organization-profile-notifications";
import { useUserOrganization } from "@/hooks/use-user-organization";
import { FixedPageHeader } from "@/components/ui/fixed-page-header";
import { PAGE_HEADERS } from "@/constants/page-headers";
import { CustomerOrderDialog } from "@/components/crm/CustomerOrderDialog";
import { ProposalNotificationDetail } from "@/components/notifications/ProposalNotificationDetail";
import { SystemNotificationsSection } from "@/components/notifications/SystemNotificationsSection";
import { OutageNotificationsSection } from "@/components/notifications/OutageNotificationsSection";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface ProposalData {
  cameras: number;
  credits: number;
  totalImages?: number;
  value: number;
  assigneeName: string;
  assigneeType: string;
  customerGenerated: boolean;
  dealType: string;
}

interface DatabaseNotification {
  id: string;
  title: string;
  proposal_data: Json;
  status: string;
  created_at: string;
  deal_id: string;
  access_token: string;
  is_edited?: boolean;
  edited_at?: string | null;
}

interface Notification {
  id: string;
  title: string;
  proposal_data: ProposalData;
  status: string;
  created_at: string;
  deal_id: string;
  access_token: string;
  is_edited: boolean;
  edited_at: string;
}

// Helper function to safely parse proposal_data with improved assignee handling
const parseProposalData = (data: Json): ProposalData => {
  console.log("Parsing proposal data:", data);
  
  if (typeof data === 'object' && data !== null) {
    const obj = data as Record<string, any>;
    
    // Better assignee name handling with fallback logic
    let assigneeName = 'Unknown Assignee';
    let assigneeType = 'Staff';
    
    if (obj.assigneeName && obj.assigneeName !== 'Unknown Assignee') {
      assigneeName = obj.assigneeName;
    } else if (obj.assigneeEmail) {
      // Use email username as fallback if full name not available
      assigneeName = obj.assigneeEmail.split('@')[0];
    }
    
    if (obj.assigneeType) {
      assigneeType = obj.assigneeType;
    }
    
    const result = {
      cameras: obj.cameras || 0,
      credits: obj.credits || 0,
        totalImages: obj.totalImages || obj.total_images,
      value: obj.value || 0,
      assigneeName: assigneeName,
      assigneeType: assigneeType,
      customerGenerated: obj.customerGenerated || false,
      dealType: obj.dealType || 'proposal'
    };
    
    console.log("Parsed proposal data result:", result);
    return result;
  }
  
  return {
    cameras: 0,
    credits: 0,
    totalImages: 0,
    value: 0,
    assigneeName: 'System',
    assigneeType: 'Staff',
    customerGenerated: false,
    dealType: 'proposal'
  };
};

export default function Notifications() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const markDealAsLost = useMarkDealAsLost();
  
  // Get user's organization ID using proper hook
  const { data: userOrganizationId, isLoading: isLoadingOrg, error: orgError } = useUserOrganization();
  const [searchParams, setSearchParams] = useSearchParams();
  const [termsAcknowledgedByNotification, setTermsAcknowledgedByNotification] = useState<Record<string, boolean>>({});
  const [proposalAcceptanceReady, setProposalAcceptanceReady] = useState<Record<string, { ready: boolean; deliveryAddressId: string | null; orderReference: string | null }>>({});
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingProposal, setEditingProposal] = useState<{
    dealId: string;
    notificationId: string;
    dealData: any;
  } | null>(null);
  const [highlightedNotificationId, setHighlightedNotificationId] = useState<string | null>(null);
  const [expandedNotificationId, setExpandedNotificationId] = useState<string | null>(null);
  
  // Organization profile notifications
  const { data: orgProfileNotifications = [] } = useOrganizationProfileNotifications();
  const dismissNotification = useDismissOrganizationProfileNotification();

  // Enhanced notification query with PENDING STATUS FILTER
  const { data: dbNotifications = [], isLoading, refetch } = useQuery({
    queryKey: ["proposal-notifications"],
    queryFn: async (): Promise<DatabaseNotification[]> => {
      try {
        console.log("🔔 [Notifications] Starting notifications query for pending proposals only");
        console.log("🔔 [Notifications] Current user:", user);
        
        if (!user) {
          console.log("🔔 [Notifications] No authenticated user");
          return [];
        }

        // Strategy 1: Direct query using user_id as contact_id - WITH PENDING FILTER
        console.log("🔔 [Notifications] Strategy 1: Direct user_id lookup for pending notifications");
        console.log("🔔 [Notifications] Looking for notifications with contact_id =", user.id);
        const { data: directData, error: directError } = await supabase
          .from('proposal_notifications')
          .select(`
            id,
            title,
            proposal_data,
            status,
            created_at,
            deal_id,
            contact_id,
            access_token,
            is_edited,
            edited_at
          `)
          .eq('contact_id', user.id)
          .in('status', ['pending', 'archived'])  // Show pending and archived notifications
          .order('created_at', { ascending: false });

        console.log("🔔 [Notifications] Strategy 1 result:", { directData, directError });

        if (directData && directData.length > 0) {
          console.log("✅ [Notifications] Found pending notifications via direct user_id:", directData.length);
          return directData;
        }

        // Strategy 2: Find all profiles linked to this user_id - WITH PENDING FILTER
        console.log("🔔 [Notifications] Strategy 2: Profile lookup by user_id for pending notifications");
        const { data: userProfiles, error: profileError } = await supabase
          .from('profiles')
          .select('id, email, first_name, last_name, user_id')
          .eq('user_id', user.id);

        if (profileError) {
          console.error("❌ [Notifications] Error fetching user profiles:", profileError);
        } else {
          console.log("📋 [Notifications] Found user profiles:", userProfiles);
          
          if (userProfiles && userProfiles.length > 0) {
            const profileIds = userProfiles.map(p => p.id);
            console.log("🔍 [Notifications] Searching for pending notifications with profile IDs:", profileIds);
            
            const { data: profileNotifications, error: profileNotifError } = await supabase
              .from('proposal_notifications')
              .select(`
                id,
                title,
                proposal_data,
                status,
                created_at,
                deal_id,
                contact_id,
                access_token,
                is_edited,
                edited_at
              `)
              .in('contact_id', profileIds)
              .in('status', ['pending', 'archived'])  // Show pending and archived notifications
              .order('created_at', { ascending: false });

            if (profileNotifications && profileNotifications.length > 0) {
              console.log("✅ [Notifications] Found pending notifications via user profiles:", profileNotifications.length);
              return profileNotifications;
            }
          }
        }

        // Strategy 3: Email-based fallback - WITH PENDING FILTER
        console.log("🔔 [Notifications] Strategy 3: Email-based profile lookup for pending notifications");
        const { data: emailProfiles, error: emailError } = await supabase
          .from('profiles')
          .select('id, email, first_name, last_name, user_id')
          .eq('email', user.email);

        if (emailError) {
          console.error("❌ [Notifications] Error fetching email profiles:", emailError);
        } else {
          console.log("📧 [Notifications] Found email-based profiles:", emailProfiles);
          
          if (emailProfiles && emailProfiles.length > 0) {
            const emailProfileIds = emailProfiles.map(p => p.id);
            console.log("🔍 [Notifications] Searching for pending notifications with email profile IDs:", emailProfileIds);
            
            const { data: emailNotifications, error: emailNotifError } = await supabase
              .from('proposal_notifications')
              .select(`
                id,
                title,
                proposal_data,
                status,
                created_at,
                deal_id,
                contact_id,
                access_token,
                is_edited,
                edited_at
              `)
              .in('contact_id', emailProfileIds)
              .in('status', ['pending', 'archived'])  // Show pending and archived notifications
              .order('created_at', { ascending: false });

            if (emailNotifications && emailNotifications.length > 0) {
              console.log("✅ [Notifications] Found pending notifications via email profiles:", emailNotifications.length);
              return emailNotifications;
            }
          }
        }

        console.log("ℹ️ [Notifications] No pending notifications found via any strategy");
        return [];
      } catch (error) {
        console.error("❌ [Notifications] Unexpected error in notifications query:", error);
        return [];
      }
    },
    retry: 1,
    staleTime: 5000,
    enabled: !!user
  });

  // Enhanced real-time subscription for notifications with deal status tracking
  useEffect(() => {
    if (!user) return;

    console.log('🔄 [Notifications] Setting up enhanced real-time subscriptions');

    // Subscription for proposal notifications
    const notificationsChannel = supabase
      .channel(`proposal-notifications-updates-${Date.now()}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'proposal_notifications'
        },
        (payload) => {
          console.log('🔄 [Notifications] Notification update detected:', payload);
          refetch();
        }
      )
      .subscribe();

    // Enhanced subscription for deal status changes (critical for won deals)
    const dealsChannel = supabase
      .channel(`notifications-page-deals-status-updates-${Date.now()}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'crm_deals'
        },
        (payload) => {
          console.log('🔄 [Notifications] Deal update detected:', payload);
          const newDeal = payload.new as any;
          const oldDeal = payload.old as any;
          
          // Specifically track status changes to 'won'
          if (newDeal?.status === 'won' && oldDeal?.status !== 'won') {
            console.log('🏆 [Notifications] Deal marked as WON, triggering aggressive cache refresh:', {
              dealId: newDeal.id,
              oldStatus: oldDeal?.status,
              newStatus: newDeal?.status
            });
            
            // Aggressive cache invalidation for won deals
            setTimeout(async () => {
              console.log('🔄 [Notifications] Executing delayed cache invalidation for won deal');
              await queryClient.invalidateQueries({ queryKey: ['deals'] });
              await queryClient.refetchQueries({ queryKey: ['deals'] });
            }, 1000);
          }
        }
      )
      .subscribe();

    return () => {
      console.log("🛑 [Notifications] Cleaning up real-time subscriptions");
      // Properly unsubscribe before removing channels
      notificationsChannel.unsubscribe().then(() => {
        supabase.removeChannel(notificationsChannel);
      });
      dealsChannel.unsubscribe().then(() => {
        supabase.removeChannel(dealsChannel);
      });
    };
  }, [user, refetch, queryClient]);

  // Convert database notifications to typed notifications with safe defaults
  const notifications: Notification[] = dbNotifications.map(dbNotification => ({
    ...dbNotification,
    proposal_data: parseProposalData(dbNotification.proposal_data),
    access_token: dbNotification.access_token,
    is_edited: Boolean(dbNotification.is_edited),
    edited_at: dbNotification.edited_at || dbNotification.created_at
  }));

  // Auto-expand single notification
  useEffect(() => {
    if (notifications.length === 1 && !expandedNotificationId) {
      setExpandedNotificationId(notifications[0].id);
    }
  }, [notifications.length, expandedNotificationId]);

  // Handle direct link parameters (token and deal from email)
  useEffect(() => {
    const token = searchParams.get('token');
    const dealId = searchParams.get('deal');
    
    if (token && dealId && notifications.length > 0) {
      console.log('🔗 Direct proposal link detected:', { token, dealId });
      
      // Find the notification that matches the token or deal ID
      const targetNotification = notifications.find(n => 
        n.access_token === token || n.deal_id === dealId
      );
      
      if (targetNotification) {
        console.log('✅ Found target notification for direct link:', targetNotification.id);
        setHighlightedNotificationId(targetNotification.id);
        setExpandedNotificationId(targetNotification.id);
        
        // Scroll to the notification after a brief delay
        setTimeout(() => {
          const element = document.getElementById(`notification-${targetNotification.id}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 500);
        
        // Show helpful toast
        toast.success(`You were invited to review this proposal: ${targetNotification.title}`, {
          duration: 5000,
        });
        
        // Clear the URL parameters after handling
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('token');
        newSearchParams.delete('deal');
        setSearchParams(newSearchParams, { replace: true });
      } else {
        console.log('⚠️ Target notification not found for direct link');
        toast.error('The proposal you were looking for could not be found or may have already been processed.');
      }
    }
  }, [notifications, searchParams, setSearchParams]);

  const handleTermsAcknowledgmentChange = (notificationId: string, acknowledged: boolean) => {
    setTermsAcknowledgedByNotification(prev => ({
      ...prev,
      [notificationId]: acknowledged
    }));
  };

  const handleProposalAcceptanceReady = (notificationId: string, ready: boolean, deliveryAddressId: string | null, orderReference: string | null) => {
    setProposalAcceptanceReady(prev => ({
      ...prev,
      [notificationId]: { ready, deliveryAddressId, orderReference }
    }));
  };

  // UPDATED ACCEPT HANDLER - Using new secure service method
  const handleAccept = async (notificationId: string, dealId: string) => {
    const acceptanceData = proposalAcceptanceReady[notificationId];
    
    if (!acceptanceData?.ready) {
      toast.error("Please select a delivery address and acknowledge the terms and conditions before accepting the proposal.");
      return;
    }

    try {
      console.log('🚀 [Notifications] Starting secure proposal acceptance with delivery address');
      
      // Use the updated service method with delivery address and order reference
      const result = await ProposalAcceptanceService.acceptProposal(
        notificationId, 
        dealId, 
        acceptanceData.deliveryAddressId,
        acceptanceData.orderReference
      );
      
      if (result.success) {
        console.log('✅ [Notifications] Proposal accepted successfully');
        
        // Aggressive cache refresh to ensure UI updates
        await queryClient.invalidateQueries({ queryKey: ['deals'] });
        await queryClient.invalidateQueries({ queryKey: ['proposal-notifications'] });
        await queryClient.refetchQueries({ queryKey: ['deals'] });
        await refetch();
        
        toast.success("Thank you for your order. You can view the progress of your order on the Orders page.");
      } else {
        throw new Error(result.error || 'Unknown error occurred');
      }
      
    } catch (error: any) {
      console.error('💥 [Notifications] Error accepting proposal:', error);
      toast.error(`Failed to accept proposal: ${error.message}`);
    }
  };

  const handleReject = async (notificationId: string, dealId: string, reason?: string) => {
    try {
      // Update proposal notification status
      const { error: notificationError } = await supabase
        .from('proposal_notifications')
        .update({
          status: 'rejected',
          responded_at: new Date().toISOString(),
          response_notes: reason || "No reason provided",
        })
        .eq('id', notificationId);

      if (notificationError) throw notificationError;

      // Mark associated deal as lost
      markDealAsLost.mutate({ 
        dealId, 
        reason: reason || undefined 
      });
      
      toast.success("Proposal declined and deal marked as lost");
      refetch();
    } catch (error) {
      console.error("Error rejecting proposal:", error);
      toast.error("Failed to reject proposal");
    }
  };

  const handleEditProposal = async (notificationId: string, dealId: string, accessToken: string) => {
    try {
      console.log('Fetching deal data for editing:', { dealId, notificationId });
      
      // Fetch the complete deal data
      const { data: deal, error } = await supabase
        .from('crm_deals')
        .select(`
          *,
          contact:profiles!crm_deals_contact_id_fkey(id, first_name, last_name, email),
          organization:organizations(id, name)
        `)
        .eq('id', dealId)
        .single();

      if (error) {
        console.error('Error fetching deal:', error);
        throw error;
      }

      if (!deal) {
        throw new Error('Deal not found');
      }

      console.log('Fetched deal data:', deal);

      // Extract data for CustomerOrderDialog
      const dealData = deal.deal_data as any || {};
      const organizationId = deal.organization?.id || userOrganizationId;
      const savedDiscountTypes = Array.isArray((dealData as any)?.discount_types)
        ? (dealData as any).discount_types
        : (dealData as any)?.discount_type && (dealData as any).discount_type !== 'none'
          ? [(dealData as any).discount_type]
          : [];
      const savedDiscountAmount = Math.max(
        Number((dealData as any)?.discount_amount) || 0,
        Number(deal.discount_amount) || 0
      );

      setEditingProposal({
        dealId,
        notificationId,
        dealData: {
          dealId,
          title: deal.title,
          orderType: (dealData as any)?.order_type || 'cameras_and_credits',
          cameraQuantity: deal.total_cameras || 1,
          credits: (dealData as any)?.credits_per_camera || 1095,
          dealValue: deal.value || 0,
          contactId: deal.contact?.id,
          organizationId,
          cameraModelId: (dealData as any)?.camera_model_id,
          plan: (dealData as any)?.plan,
          paymentStructure: (dealData as any)?.payment_structure,
          commitmentYears: (dealData as any)?.commitment_years,
          customAiModel: Boolean((dealData as any)?.custom_ai_model),
          discountAmount: savedDiscountAmount,
          discountTypes: savedDiscountTypes,
          accessories: Array.isArray((dealData as any)?.accessories) ? (dealData as any).accessories : [],
          deal_data: dealData
        }
      });

      setEditDialogOpen(true);
      toast.success("Opening proposal for editing...");
    } catch (error) {
      console.error("Error opening proposal for editing:", error);
      toast.error("Failed to open proposal for editing");
    }
  };

  const handleEditDialogAccept = async (dealId: string, deliveryAddressId: string | null, orderReference: string | null, updatedProposalData?: any) => {
    if (!editingProposal) return;
    
    try {
      console.log('🚀 [Notifications] Accepting proposal via edit dialog');
      
      const result = await ProposalAcceptanceService.acceptProposal(
        editingProposal.notificationId, 
        dealId, 
        deliveryAddressId,
        orderReference,
        updatedProposalData
      );
      
      if (result.success) {
        console.log('✅ [Notifications] Proposal accepted successfully via edit dialog');
        
        // Close the dialog
        setEditDialogOpen(false);
        setEditingProposal(null);
        
        // Refresh data
        await queryClient.invalidateQueries({ queryKey: ['deals'] });
        await queryClient.invalidateQueries({ queryKey: ['proposal-notifications'] });
        await refetch();
        
        toast.success("Thank you for your order. You can view the progress of your order on the Orders page.");
      } else {
        throw new Error(result.error || 'Unknown error occurred');
      }
      
    } catch (error: any) {
      console.error('💥 [Notifications] Error accepting proposal via edit dialog:', error);
      toast.error(`Failed to accept proposal: ${error.message}`);
    }
  };

  const handleRefresh = () => {
    refetch();
    toast.success("Notifications refreshed");
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);

  };

  if (isLoading) {
    return (
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          <FixedPageHeader {...PAGE_HEADERS.adminNotifications} />
          <div className="p-8 space-y-8" style={{ paddingTop: "8.5rem" }}>
            <div className="text-center py-8">Loading notifications...</div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    );
  }

  // Organization ID is now fetched via useUserOrganization hook above
  console.log('🏢 [Notifications] User organization ID:', userOrganizationId);
  console.log('🏢 [Notifications] Organization loading state:', isLoadingOrg);
  if (orgError) {
    console.error('❌ [Notifications] Error fetching organization:', orgError);
  }

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <FixedPageHeader
          {...PAGE_HEADERS.adminNotifications}
          actions={
              <div className="flex items-center gap-2">
                {(notifications.length > 0 || orgProfileNotifications.length > 0) && (
                  <Badge variant="secondary" className="bg-white/20 text-white">
                    {notifications.filter((n: Notification) => n.status === 'pending').length + orgProfileNotifications.length} pending
                  </Badge>
                )}
                <Button
                  onClick={handleRefresh}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2 bg-white/10 border-white/20 text-white hover:bg-white/20"
                >
                  <RefreshCw className="h-4 w-4" />
                  Refresh
                </Button>
              </div>
            }
          />
        <div className="p-8 space-y-8" style={{ paddingTop: "8rem" }}>
          <Tabs defaultValue="proposals" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="proposals">Proposals</TabsTrigger>
              <TabsTrigger value="system">System Updates</TabsTrigger>
              <TabsTrigger value="outages">Outages</TabsTrigger>
            </TabsList>

            <TabsContent value="proposals" className="space-y-4 mt-4">
              {/* Organization Profile Completion Notifications */}
              {orgProfileNotifications.length > 0 && (
            <div className="space-y-4 mb-6">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Building className="h-5 w-5" />
                Organization Profile
              </h2>
              {orgProfileNotifications.map((notification) => (
                <Card key={notification.id} className="border-teal-light" style={{ backgroundColor: '#F0EDE6' }}>
                  <CardContent 
                    className="pt-6 p-6 mb-6 border-teal-light rounded-lg" 
                    style={{ backgroundColor: '#F0EDE6' }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold mb-2 text-left" style={{ color: '#321521' }}>
                          Complete Your Organisation Profile
                        </h3>
                        <p className="mb-4" style={{ color: '#321521' }}>
                          Please complete your organisation profile to help us provide better service. This information will help our team understand your business needs.
                        </p>
                        <div className="flex gap-2">
                          <Button 
                            asChild
                             className="text-black hover:opacity-90"
                             style={{ backgroundColor: '#DFEF97' }}
                          >
                            <Link to="/dashboard/organisation-profile">
                              Complete Profile
                            </Link>
                          </Button>
                          <Button
                            variant="outline"
                            onClick={async () => {
                              try {
                                await dismissNotification(notification.id);
                                queryClient.invalidateQueries({ queryKey: ["organization-profile-notifications"] });
                                toast.success("Notification dismissed");
                              } catch (error) {
                                toast.error("Failed to dismiss notification");
                              }
                            }}
                          >
                            Dismiss
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {notifications.length === 0 && orgProfileNotifications.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Bell className="h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No proposal notifications</h3>
                <p className="text-gray-500">You're all caught up! New proposal notifications will appear here.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              <Accordion
                type="single" 
                collapsible 
                value={expandedNotificationId || undefined}
                onValueChange={setExpandedNotificationId}
                className="space-y-2"
              >
                {notifications.map((notification: Notification) => (
                  <AccordionItem 
                    key={notification.id} 
                    value={notification.id}
                    className={`border rounded-lg ${highlightedNotificationId === notification.id ? 'ring-2 ring-primary ring-opacity-50 shadow-lg' : ''}`}
                  >
                    <AccordionTrigger className="px-4 py-3 hover:no-underline">
                      <div className="flex items-center justify-between w-full text-left">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-semibold">{notification.title}</h3>
                            {notification.is_edited && (
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                                Edited
                              </Badge>
                            )}
                            <StatusBadge status={notification.status} />
                          </div>
                          <div className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                            <Clock className="h-3 w-3" />
                            <span>{formatDate(notification.created_at)}</span>
                            {notification.edited_at && notification.edited_at !== notification.created_at && (
                              <span className="text-blue-600 ml-2">
                                • Last edited: {formatDate(notification.edited_at)}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground mt-1">
                            {notification.proposal_data.cameras} cameras • {formatCurrency(notification.proposal_data.value)} ex VAT
                          </div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-0 pb-0">
                      <ProposalNotificationDetail
                        notification={notification}
                        userOrganizationId={userOrganizationId}
                        proposalAcceptanceReady={proposalAcceptanceReady}
                        highlightedNotificationId={highlightedNotificationId}
                        onAccept={handleAccept}
                        onReject={handleReject}
                        onEdit={handleEditProposal}
                        onProposalAcceptanceReady={handleProposalAcceptanceReady}
                        formatDate={formatDate}
                        formatCurrency={formatCurrency}
                      />
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}
            </TabsContent>

            <TabsContent value="system" className="mt-4">
              <SystemNotificationsSection />
            </TabsContent>

            <TabsContent value="outages" className="mt-4">
              <OutageNotificationsSection />
            </TabsContent>
          </Tabs>
          </div>

          {/* Edit Proposal Dialog */}
          {editingProposal && (
            <CustomerOrderDialog
              open={editDialogOpen}
              onOpenChange={setEditDialogOpen}
              mode="edit"
              initialData={editingProposal.dealData}
              onAcceptProposal={handleEditDialogAccept}
            />
          )}
        </SidebarInset>
      </SidebarProvider>
    );
  }

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'pending':
      return (
        <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
          Pending Response
        </Badge>
      );
    case 'accepted':
      return (
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
          Accepted
        </Badge>
      );
    case 'rejected':
      return (
        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
          Declined
        </Badge>
      );
    case 'archived':
      return (
        <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">
          Archived
        </Badge>
      );
    default:
      return null;
  }
}
